/**
 * @module ReasoningRunningState
 * @purpose In-progress state for the reasoning panel — a run is pending or running.
 * @depends react-i18next, components/common (EvidenxSpinner)
 * @dependedOnBy CanvasReasoningPanel
 *
 * @rules
 * - Performance: pure display — no data fetching
 * - State: none
 * - Errors: n/a
 */

import { useTranslation } from 'react-i18next'
import { EvidenxSpinner } from '../../../../components/common'

/**
 * Renders the reasoning panel in-progress state.
 *
 * @behaviour Distinct from the empty state. While a run is `pending`/`running`
 *   its clusterCount is null, which previously resolved to "No clusters
 *   surfaced" — telling the analyst there was nothing there for the several
 *   minutes the engine was working. FR1a: the block spinner sits large and
 *   TOP-ANCHORED in the panel body (user feedback 2026-09-04: vertical
 *   centring in a tall panel pushed it below the visible fold; top placement
 *   keeps it unmissable in any viewport). `aria-busy` announces progress to
 *   assistive tech.
 * @errors n/a — purely presentational
 */
export function ReasoningRunningState(): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div
      role="status"
      aria-busy="true"
      className="flex flex-col items-center gap-4 px-4 pt-10 pb-8"
    >
      <EvidenxSpinner size="block" label={t('common:busy.label', 'Working…')} />
      <p className="text-base font-semibold" style={{ color: 'var(--text)' }}>
        {t('reasoning.running.title')}
      </p>
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        {t('reasoning.running.subtitle')}
      </p>
    </div>
  )
}
